import { useState, useEffect, useMemo, useRef } from "react";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, MessageCircle, CalendarDays, Users, User, Mail, Phone, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useExcursions } from "@/hooks/useExcursions";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useToast } from "@/hooks/use-toast";
import { useSearchParams } from "react-router-dom";
import { SmartImage } from "@/components/SmartImage";
import { MagneticButton } from "@/components/motion/MagneticButton";
import { cn } from "@/lib/utils";

// ---------- Schemas (one per step) ----------
const step1 = z.object({ excursions: z.array(z.string()).min(1) });
const step2 = z.object({
  date: z.string().min(1),
  participants: z.string().min(1),
});
const step3 = z.object({
  name: z.string().trim().min(2).max(100),
  email: z.string().trim().email().max(255),
  whatsapp: z.string().trim().max(20).optional().or(z.literal("")),
});
const step4 = z.object({
  message: z.string().trim().max(1000).optional().or(z.literal("")),
  consent: z.literal(true),
});

type WizardData = {
  excursions: string[];
  date: string;
  participants: string;
  name: string;
  email: string;
  whatsapp: string;
  message: string;
  consent: boolean;
};

const STORAGE_KEY = "contact_wizard_v1";
const TTL_MS = 24 * 60 * 60 * 1000;

const initial: WizardData = {
  excursions: [],
  date: "",
  participants: "",
  name: "",
  email: "",
  whatsapp: "",
  message: "",
  consent: false,
};

const STEPS = ["Expérience", "Date & groupe", "Vos coordonnées", "Récapitulatif"];

export const ContactWizard = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(0);
  const [data, setData] = useState<WizardData>(initial);
  const [direction, setDirection] = useState(1);
  const { excursions, loading } = useExcursions();
  const { trackContactSubmit } = useAnalytics();
  const { toast } = useToast();
  const containerRef = useRef<HTMLDivElement>(null);

  // Restore from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.ts && Date.now() - parsed.ts < TTL_MS && parsed.data) {
          const restored = { ...parsed.data };
          // Migration: ancien format string -> nouveau format array
          if (typeof restored.excursion === "string") {
            restored.excursions = restored.excursion ? [restored.excursion] : [];
            delete restored.excursion;
          }
          if (!Array.isArray(restored.excursions)) restored.excursions = [];
          setData((d) => ({ ...d, ...restored, consent: false }));
        }
      }
    } catch {}
    const exParam = searchParams.get("excursion");
    if (exParam) {
      setData((d) => ({ ...d, excursions: [exParam] }));
      setStep(1);
    }
  }, [searchParams]);

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ts: Date.now(), data: { ...data, consent: false } }));
    } catch {}
  }, [data]);

  const update = <K extends keyof WizardData>(key: K, value: WizardData[K]) =>
    setData((d) => ({ ...d, [key]: value }));

  const stepValid = useMemo(() => {
    switch (step) {
      case 0: return step1.safeParse(data).success;
      case 1: return step2.safeParse(data).success;
      case 2: return step3.safeParse(data).success;
      case 3: return step4.safeParse(data).success;
      default: return false;
    }
  }, [step, data]);

  const goNext = () => {
    if (!stepValid) return;
    setDirection(1);
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    requestAnimationFrame(() => containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  const goPrev = () => {
    setDirection(-1);
    setStep((s) => Math.max(0, s - 1));
  };
  const goTo = (i: number) => {
    if (i <= step) { setDirection(i > step ? 1 : -1); setStep(i); }
  };

  const submit = () => {
    const excList = data.excursions.length > 1
      ? `les excursions suivantes :\n· ${data.excursions.join("\n· ")}`
      : `« ${data.excursions[0]} »`;
    const message = `Bonjour Cléa ! Je souhaite réserver ${excList}\n\nDate : ${data.date}\nParticipants : ${data.participants}\n\nMon nom : ${data.name}\nEmail : ${data.email}${data.whatsapp ? `\nWhatsApp : ${data.whatsapp}` : ""}${data.message ? `\n\nMessage : ${data.message}` : ""}\n\nMerci !`;
    const url = `https://wa.me/5521976795050?text=${encodeURIComponent(message)}`;
    trackContactSubmit(data.excursions.join(", "), "whatsapp");
    window.open(url, "_blank");
    toast({ title: "Demande prête", description: "Votre conversation WhatsApp est ouverte." });
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  const published = excursions.filter((e) => e.actif && e.is_published);
  const selectedExcursion = published.find((e) => e.title === (data.excursions?.[0] ?? ""));
  const toggleExcursion = (title: string) => {
    setData((d) => {
      // "À déterminer ensemble" est exclusif
      if (title === "À déterminer ensemble") {
        return { ...d, excursions: d.excursions.includes(title) ? [] : [title] };
      }
      const without = d.excursions.filter((t) => t !== "À déterminer ensemble");
      return {
        ...d,
        excursions: without.includes(title)
          ? without.filter((t) => t !== title)
          : [...without, title],
      };
    });
  };

  const variants = {
    enter: (d: number) => ({ opacity: 0, x: d > 0 ? 30 : -30 }),
    center: { opacity: 1, x: 0 },
    exit: (d: number) => ({ opacity: 0, x: d > 0 ? -30 : 30 }),
  };

  return (
    <div ref={containerRef} className="glass-card rounded-2xl p-5 sm:p-8 md:p-10">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3 text-xs uppercase tracking-wider text-muted-foreground">
          <span>Étape {step + 1} / {STEPS.length}</span>
          <span className="text-foreground font-semibold">{STEPS[step]}</span>
        </div>
        <div className="relative h-1.5 bg-muted rounded-full overflow-hidden">
          <motion.div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-secondary rounded-full"
            initial={false}
            animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            transition={{ type: "spring", stiffness: 120, damping: 20 }}
          />
        </div>
        <div className="mt-3 grid grid-cols-4 gap-2">
          {STEPS.map((label, i) => (
            <button
              key={label}
              type="button"
              onClick={() => goTo(i)}
              disabled={i > step}
              className={cn(
                "text-[11px] sm:text-xs font-medium text-left transition-colors",
                i < step && "text-primary cursor-pointer hover:underline",
                i === step && "text-foreground",
                i > step && "text-muted-foreground/50 cursor-not-allowed"
              )}
            >
              <span className="hidden sm:inline">{i < step ? "✓ " : `${i + 1}. `}</span>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Steps */}
      <div className="min-h-[340px]" aria-live="polite">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            {step === 0 && (
              <div>
                <h3 className="font-league uppercase text-3xl sm:text-4xl mb-2 leading-[0.95]">
                  Quelle <span className="text-gradient-roca">expérience</span> vous tente ?
                </h3>
                <p className="text-muted-foreground mb-6">Sélectionnez une ou plusieurs expériences · ou laissez-moi vous guider.</p>
                {loading ? (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div key={i} className="aspect-[5/2] bg-muted rounded-xl animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3 max-h-[420px] overflow-y-auto pr-1">
                    {published.map((exc) => {
                      const active = data.excursions.includes(exc.title);
                      return (
                        <button
                          key={exc.id}
                          type="button"
                          onClick={() => toggleExcursion(exc.title)}
                          className={cn(
                            "group relative overflow-hidden rounded-xl border-2 text-left transition-all",
                            active ? "border-primary shadow-float" : "border-transparent hover:border-border"
                          )}
                        >
                          <div className="relative aspect-[5/2]">
                            <SmartImage src={(exc as any).wizard_image || exc.cover_image || exc.images?.[0]} alt={exc.title} aspectClassName="absolute inset-0 w-full h-full" sizes="280px" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                            <div className="absolute inset-0 flex items-end p-3">
                              <span className="font-league uppercase text-white text-lg sm:text-xl leading-tight drop-shadow">
                                {exc.title}
                              </span>
                            </div>
                            {exc.tags?.coup_de_coeur && (
                              <span className="absolute top-2 left-2 bg-primary text-primary-foreground text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full">
                                Coup de cœur
                              </span>
                            )}
                            {active && (
                              <span className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                                <Check className="w-3 h-3" />
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => toggleExcursion("À déterminer ensemble")}
                      className={cn(
                        "rounded-xl border-2 border-dashed p-5 flex items-center gap-3 text-left transition-all",
                        data.excursions.includes("À déterminer ensemble") ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40"
                      )}
                    >
                      <Sparkles className="w-5 h-5 text-primary flex-shrink-0" />
                      <div>
                        <div className="font-semibold">Je ne sais pas encore</div>
                        <div className="text-sm text-muted-foreground">Cléa vous proposera la meilleure option.</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            )}

            {step === 1 && (
              <div className="space-y-8">
                <div>
                  <h3 className="font-league uppercase text-3xl sm:text-4xl mb-2 leading-[0.95]">
                    Quand & <span className="text-gradient-roca">combien</span> ?
                  </h3>
                  <p className="text-muted-foreground">Donnez-moi vos préférences · tout reste flexible.</p>
                </div>

                <div>
                  <Label className="flex items-center gap-2 text-base mb-3"><CalendarDays className="w-4 h-4 text-primary" /> Date souhaitée</Label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {[
                      { label: "Cette semaine", days: 3 },
                      { label: "Ce week-end", days: 5 },
                      { label: "Le mois prochain", days: 30 },
                    ].map((q) => {
                      const d = new Date();
                      d.setDate(d.getDate() + q.days);
                      const iso = d.toISOString().slice(0, 10);
                      const active = data.date === iso;
                      return (
                        <button
                          key={q.label}
                          type="button"
                          onClick={() => update("date", iso)}
                          className={cn(
                            "px-4 py-2 rounded-full text-sm border transition-all",
                            active ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground/40"
                          )}
                        >
                          {q.label}
                        </button>
                      );
                    })}
                  </div>
                  <Input
                    type="date"
                    value={data.date}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => update("date", e.target.value)}
                    className="h-12 text-base"
                  />
                </div>

                <div>
                  <Label className="flex items-center gap-2 text-base mb-3"><Users className="w-4 h-4 text-primary" /> Nombre de participants</Label>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => {
                      const v = String(n);
                      const active = data.participants === v;
                      return (
                        <button
                          key={n}
                          type="button"
                          onClick={() => update("participants", v)}
                          className={cn(
                            "min-w-12 h-12 px-4 rounded-full text-base font-semibold border transition-all",
                            active ? "bg-primary text-primary-foreground border-primary scale-105" : "border-border hover:border-foreground/40"
                          )}
                        >
                          {n}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => update("participants", "9+")}
                      className={cn(
                        "min-w-16 h-12 px-4 rounded-full text-base font-semibold border transition-all",
                        data.participants === "9+" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-foreground/40"
                      )}
                    >
                      9 +
                    </button>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-league uppercase text-3xl sm:text-4xl mb-2 leading-[0.95]">
                    Vos <span className="text-gradient-roca">coordonnées</span>
                  </h3>
                  <p className="text-muted-foreground">Pour vous répondre rapidement et personnellement.</p>
                </div>

                <div>
                  <Label htmlFor="w-name" className="flex items-center gap-2 mb-2"><User className="w-4 h-4 text-primary" /> Nom complet *</Label>
                  <Input id="w-name" value={data.name} onChange={(e) => update("name", e.target.value)} placeholder="Jean Dupont" className="h-12 text-base" autoFocus />
                </div>
                <div>
                  <Label htmlFor="w-email" className="flex items-center gap-2 mb-2"><Mail className="w-4 h-4 text-primary" /> Email *</Label>
                  <Input id="w-email" type="email" value={data.email} onChange={(e) => update("email", e.target.value)} placeholder="jean.dupont@email.com" className="h-12 text-base" />
                </div>
                <div>
                  <Label htmlFor="w-wa" className="flex items-center gap-2 mb-2"><Phone className="w-4 h-4 text-primary" /> WhatsApp <span className="text-xs text-muted-foreground font-normal">(recommandé pour réponse rapide)</span></Label>
                  <Input id="w-wa" value={data.whatsapp} onChange={(e) => update("whatsapp", e.target.value)} placeholder="+33 6 12 34 56 78" className="h-12 text-base" />
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-league uppercase text-3xl sm:text-4xl mb-2 leading-[0.95]">
                    Tout est <span className="text-gradient-roca">prêt</span>
                  </h3>
                  <p className="text-muted-foreground">Vérifiez votre demande et envoyez-la sur WhatsApp.</p>
                </div>

                <div className="rounded-xl border bg-card overflow-hidden">
                  {(((selectedExcursion as any)?.wizard_image) || selectedExcursion?.cover_image) && data.excursions.length === 1 && (
                    <div className="relative h-40">
                      <SmartImage src={(selectedExcursion as any).wizard_image || selectedExcursion!.cover_image} alt={selectedExcursion!.title} aspectClassName="absolute inset-0 w-full h-full" sizes="600px" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                      <div className="absolute bottom-3 left-4 right-4 font-league uppercase text-white text-2xl leading-tight drop-shadow">
                        {data.excursions[0]}
                      </div>
                    </div>
                  )}
                  <dl className="p-5 grid grid-cols-2 gap-4 text-sm">
                    <div className="col-span-2">
                      <dt className="text-muted-foreground text-xs uppercase tracking-wider">
                        {data.excursions.length > 1 ? `Excursions (${data.excursions.length})` : "Excursion"}
                      </dt>
                      <dd className="font-medium mt-1">
                        {data.excursions.length > 1 ? (
                          <ul className="space-y-0.5">
                            {data.excursions.map((t) => <li key={t}>· {t}</li>)}
                          </ul>
                        ) : data.excursions[0]}
                      </dd>
                    </div>
                    <div><dt className="text-muted-foreground text-xs uppercase tracking-wider">Date</dt><dd className="font-medium mt-1">{data.date}</dd></div>
                    <div><dt className="text-muted-foreground text-xs uppercase tracking-wider">Participants</dt><dd className="font-medium mt-1">{data.participants}</dd></div>
                    <div><dt className="text-muted-foreground text-xs uppercase tracking-wider">Nom</dt><dd className="font-medium mt-1">{data.name}</dd></div>
                    <div><dt className="text-muted-foreground text-xs uppercase tracking-wider">Email</dt><dd className="font-medium mt-1 truncate">{data.email}</dd></div>
                  </dl>
                </div>

                <div>
                  <Label htmlFor="w-msg" className="mb-2 block">Message complémentaire (optionnel)</Label>
                  <Textarea id="w-msg" rows={3} value={data.message} onChange={(e) => update("message", e.target.value)} placeholder="Demandes spéciales, niveau, langues..." />
                </div>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={data.consent}
                    onChange={(e) => update("consent", e.target.checked)}
                    className="mt-1"
                  />
                  <span className="text-sm text-muted-foreground">
                    J'accepte que mes données soient utilisées pour traiter ma demande (
                    <a href="/confidentialite" className="text-primary underline">politique de confidentialité</a>) *
                  </span>
                </label>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Nav */}
      <div className="mt-8 flex items-center justify-between gap-3">
        <Button
          type="button"
          variant="ghost"
          onClick={goPrev}
          disabled={step === 0}
          className="gap-2"
        >
          <ChevronLeft className="w-4 h-4" /> Précédent
        </Button>

        {step < STEPS.length - 1 ? (
          <MagneticButton>
            <Button type="button" onClick={goNext} disabled={!stepValid} size="lg" className="gap-2 rounded-full px-6">
              Suivant <ChevronRight className="w-4 h-4" />
            </Button>
          </MagneticButton>
        ) : (
          <MagneticButton>
            <Button
              type="button"
              onClick={submit}
              disabled={!stepValid}
              size="lg"
              className="gap-2 rounded-full px-8 bg-green-600 hover:bg-green-700 text-white shadow-float"
            >
              <MessageCircle className="w-5 h-5" /> Envoyer sur WhatsApp
            </Button>
          </MagneticButton>
        )}
      </div>
    </div>
  );
};

export default ContactWizard;